;(function () {
	const SECTION_SELECTOR = '[data-multi-collection-catalog]'
	const PRODUCT_SELECTOR = '[data-catalog-product]'

	class MultiCollectionCatalog {
		constructor(section) {
			this.section = section
			this.searchInput = section.querySelector('[data-catalog-search]')
			this.collectionSelect = section.querySelector('[data-catalog-collection]')
			this.sortSelect = section.querySelector('[data-catalog-sort]')
			this.clearButton = section.querySelector('[data-catalog-clear]')
			this.countElement = section.querySelector('[data-catalog-count]')
			this.tagsElement = section.querySelector('[data-catalog-tags]')
			this.gridElement = section.querySelector('[data-catalog-grid]')
			this.emptyElement = section.querySelector('[data-catalog-empty]')
			this.sourceElement = section.querySelector('[data-catalog-source]')
			this.products = []
		}

		init() {
			if (!this.sourceElement || !this.gridElement) return
			this.products = this.collectProducts()
			this.populateCollections()
			this.bindEvents()
			this.applyFilters()
		}

		collectProducts() {
			const seen = new Set()

			return Array.from(this.sourceElement.querySelectorAll(PRODUCT_SELECTOR))
				.filter((item) => {
					const productId = item.dataset.productId
					if (!productId || seen.has(productId)) return false
					seen.add(productId)
					return true
				})
				.map((item) => ({
					id: item.dataset.productId,
					title: (item.dataset.productTitle || '').trim(),
					price: Number(item.dataset.productPrice || 0),
					collectionHandle: item.dataset.collectionHandle || '',
					collectionTitle: item.dataset.collectionTitle || '',
					collectionOrder: Number(item.dataset.collectionOrder || 0),
					popularityRank: Number(item.dataset.popularityRank || 0),
					node: item,
				}))
		}

		populateCollections() {
			if (!this.collectionSelect) return

			const collections = []
			const seen = new Set()

			this.products.forEach((product) => {
				if (!product.collectionHandle || seen.has(product.collectionHandle)) return
				seen.add(product.collectionHandle)
				collections.push({
					handle: product.collectionHandle,
					title: product.collectionTitle,
					order: product.collectionOrder,
				})
			})

			collections.sort((left, right) => left.order - right.order)

			collections.forEach((collection) => {
				const option = document.createElement('option')
				option.value = collection.handle
				option.textContent = collection.title
				this.collectionSelect.appendChild(option)
			})
		}

		bindEvents() {
			this.searchInput?.addEventListener('input', () => this.applyFilters())
			this.collectionSelect?.addEventListener('change', () => this.applyFilters())
			this.sortSelect?.addEventListener('change', () => this.applyFilters())
			this.clearButton?.addEventListener('click', () => {
				if (this.searchInput) this.searchInput.value = ''
				if (this.collectionSelect) this.collectionSelect.value = ''
				if (this.sortSelect) this.sortSelect.value = 'collection-order'
				this.applyFilters()
			})
		}

		getFilteredProducts() {
			const searchValue = this.searchInput?.value.trim().toLowerCase() || ''
			const collectionValue = this.collectionSelect?.value || ''
			const sortValue = this.sortSelect?.value || 'collection-order'

			const filtered = this.products.filter((product) => {
				const matchesSearch =
					!searchValue || product.title.toLowerCase().includes(searchValue)
				const matchesCollection =
					!collectionValue || product.collectionHandle === collectionValue

				return matchesSearch && matchesCollection
			})

			filtered.sort((left, right) => {
				switch (sortValue) {
					case 'price-ascending':
						return left.price - right.price
					case 'price-descending':
						return right.price - left.price
					case 'title-ascending':
						return left.title.localeCompare(right.title)
					case 'title-descending':
						return right.title.localeCompare(left.title)
					case 'popularity':
						return (
							left.collectionOrder - right.collectionOrder ||
							left.popularityRank - right.popularityRank
						)
					case 'collection-order':
					default:
						return (
							left.collectionOrder - right.collectionOrder ||
							left.title.localeCompare(right.title)
						)
				}
			})

			return filtered
		}

		applyFilters() {
			const filtered = this.getFilteredProducts()
			this.gridElement.innerHTML = ''
			filtered.forEach((product) => {
				this.gridElement.appendChild(product.node)
			})

			if (this.countElement) {
				this.countElement.textContent = `${filtered.length} ${
					filtered.length === 1 ? 'product' : 'products'
				}`
			}

			this.renderTags()

			if (this.emptyElement) this.emptyElement.hidden = filtered.length !== 0
			this.gridElement.hidden = filtered.length === 0
		}

		renderTags() {
			if (!this.tagsElement) return
			this.tagsElement.innerHTML = ''

			const searchValue = this.searchInput?.value.trim() || ''
			const collectionValue = this.collectionSelect?.value || ''
			const sortValue = this.sortSelect?.value || 'collection-order'
			const collectionOption =
				this.collectionSelect?.options[this.collectionSelect.selectedIndex]
			const sortOption = this.sortSelect?.options[this.sortSelect.selectedIndex]

			if (searchValue) {
				this.tagsElement.appendChild(
					this.createTag(`Search: ${searchValue}`, () => {
						this.searchInput.value = ''
						this.applyFilters()
					})
				)
			}

			if (collectionValue && collectionOption) {
				this.tagsElement.appendChild(
					this.createTag(`Collection: ${collectionOption.text}`, () => {
						this.collectionSelect.value = ''
						this.applyFilters()
					})
				)
			}

			if (sortValue !== 'collection-order' && sortOption) {
				this.tagsElement.appendChild(
					this.createTag(`Sort: ${sortOption.text}`, () => {
						this.sortSelect.value = 'collection-order'
						this.applyFilters()
					})
				)
			}

			this.tagsElement.hidden = this.tagsElement.childElementCount === 0
		}

		createTag(label, onRemove) {
			const tag = document.createElement('span')
			tag.className = 'multi-collection-catalog__tag'
			tag.innerHTML = `<span>${label}</span>`

			const button = document.createElement('button')
			button.type = 'button'
			button.setAttribute('aria-label', `Remove ${label}`)
			button.textContent = '×'
			button.addEventListener('click', onRemove)

			tag.appendChild(button)
			return tag
		}
	}

	function initCatalogs(root) {
		const sections = Array.from((root || document).querySelectorAll(SECTION_SELECTOR))
		sections.forEach((section) => {
			if (section.multiCollectionCatalog) return
			section.multiCollectionCatalog = new MultiCollectionCatalog(section)
			section.multiCollectionCatalog.init()
		})
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => initCatalogs())
	} else {
		initCatalogs()
	}

	document.addEventListener('shopify:section:load', (event) => {
		initCatalogs(event.target)
	})
})()
